import { cleanObject } from '@shared/utils';
import collab from '@/redux/requests/collab.js';
import { del, get, post, put } from '@/redux/requests/api.js';

export const getRoles = ({ companyId } = {}, callback) => {
  return get('/v1/roles', {
    action: 'GET_ROLES',
    loader: 'getRoles',
    callback,
    query: cleanObject(
      {
        companyId
      },
      { strict: true }
    )
  });
};

export const createRole = ({ name, description } = {}, callback) => {
  return post('/v1/roles', {
    action: 'CREATE_ROLE',
    loader: 'createRole',
    callback,
    data: cleanObject(
      {
        name,
        description
      },
      { strict: true }
    )
  });
};

export const updateRole = ({ roleId, name, description } = {}, callback) => {
  const normalizedRoleId = String(roleId || '').trim();

  return put(`/v1/roles/${encodeURIComponent(normalizedRoleId)}`, {
    action: 'UPDATE_ROLE',
    loader: 'updateRole',
    callback,
    meta: {
      roleId: normalizedRoleId
    },
    data: cleanObject(
      {
        name,
        description
      },
      { strict: true }
    )
  });
};

export const deleteRole = ({ roleId } = {}, callback) => {
  const normalizedRoleId = String(roleId || '').trim();

  return del(`/v1/roles/${encodeURIComponent(normalizedRoleId)}`, {
    action: 'DELETE_ROLE',
    loader: 'deleteRole',
    callback,
    meta: {
      roleId: normalizedRoleId
    }
  });
};

export const getRoleApps = ({ roleId } = {}, callback) => {
  const normalizedRoleId = String(roleId || '').trim();

  return get(`/v1/pieve/roles/${encodeURIComponent(normalizedRoleId)}/apps`, {
    action: 'GET_ROLE_APPS',
    loader: 'getRoleApps',
    callback,
    meta: {
      roleId: normalizedRoleId
    }
  });
};

export const getRoleCapabilities = ({ roleId } = {}, callback) => {
  const normalizedRoleId = String(roleId || '').trim();
  const requestStartedAt = Date.now();

  return get(`/v1/pieve/roles/${encodeURIComponent(normalizedRoleId)}/capabilities`, {
    action: 'GET_ROLE_CAPABILITIES',
    loader: 'getRoleCapabilities',
    callback,
    meta: {
      roleId: normalizedRoleId,
      requestStartedAt
    }
  });
};

export const updateRoleCapabilities = ({ roleId, capabilities } = {}, callback) => {
  const normalizedRoleId = String(roleId || '').trim();

  return collab.put('roleCapability', {
    action: 'UPDATE_ROLE_CAPABILITIES',
    loader: 'updateRoleCapabilities',
    callback,
    resourceId: normalizedRoleId,
    meta: {
      roleId: normalizedRoleId
    },
    data: {
      capabilities: capabilities || {}
    }
  });
};

export const getRoleAppModels = ({ roleId, appId } = {}, callback) => {
  const normalizedRoleId = String(roleId || '').trim();
  const normalizedAppId = String(appId || '').trim();

  return get(`/v1/pieve/roles/${encodeURIComponent(normalizedRoleId)}/apps/${encodeURIComponent(normalizedAppId)}/models`, {
    action: 'GET_ROLE_APP_MODELS',
    loader: 'getRoleAppModels',
    callback,
    meta: {
      roleId: normalizedRoleId,
      appId: normalizedAppId
    }
  });
};

/* The catalog of permission keys a company role may hold, as `main.permission` rows. */
export const getPermissionCatalog = (callback) => {
  return get('/v1/permissions', {
    action: 'GET_PERMISSION_CATALOG',
    loader: 'getPermissionCatalog',
    callback,
    query: {
      onlyNonAdmin: 'true'
    }
  });
};

/* Every permission grant of every role this organization can see, as role-permission rows. */
export const getRolePermissionGrants = (callback) => {
  return get('/v1/role-permissions', {
    action: 'GET_ROLE_PERMISSION_GRANTS',
    loader: 'getRolePermissionGrants',
    callback
  });
};

export const createRolePermissionGrant = ({ roleId, permissionId } = {}, callback) => {
  const normalizedRoleId = String(roleId || '').trim();
  const normalizedPermissionId = String(permissionId || '').trim();

  return post('/v1/role-permissions', {
    action: 'CREATE_ROLE_PERMISSION_GRANT',
    loader: 'createRolePermissionGrant',
    callback,
    meta: {
      roleId: normalizedRoleId,
      permissionId: normalizedPermissionId
    },
    data: {
      roleId: normalizedRoleId,
      permissionId: normalizedPermissionId
    }
  });
};

export const deleteRolePermissionGrant = ({ roleId, permissionId } = {}, callback) => {
  const normalizedRoleId = String(roleId || '').trim();
  const normalizedPermissionId = String(permissionId || '').trim();

  return del('/v1/role-permissions', {
    action: 'DELETE_ROLE_PERMISSION_GRANT',
    loader: 'deleteRolePermissionGrant',
    callback,
    meta: {
      roleId: normalizedRoleId,
      permissionId: normalizedPermissionId
    },
    data: {
      roleId: normalizedRoleId,
      permissionId: normalizedPermissionId
    }
  });
};
